import { deepEqual } from '@shared/utils/deepEqual';

import { toEditorPolicies, toPutPayload } from './editorMappers';
import {
  IRoutingConfigPolicy,
  IRoutingConfigVersion,
  PUBLISH_COMMENT_MAX_LENGTH,
  PUBLISH_COMMENT_MIN_LENGTH,
} from '../model/types';

// The publish comment is the audit entry for the switch: required here, and kept
// inside the backend's length window on the trimmed text. Returns the message to
// show, or null when the comment can be sent.
export const validatePublishComment = (comment: string): string | null => {
  const trimmed = comment.trim();

  if (!trimmed) return 'A comment is required.';
  if (trimmed.length < PUBLISH_COMMENT_MIN_LENGTH) {
    return `Comment must be at least ${PUBLISH_COMMENT_MIN_LENGTH} characters.`;
  }
  if (trimmed.length > PUBLISH_COMMENT_MAX_LENGTH) {
    return `Comment must be at most ${PUBLISH_COMMENT_MAX_LENGTH} characters.`;
  }

  return null;
};

// The FE-side stand-in for the backend's dropped 409: the version active now is not
// the one this draft was branched from, so publishing would archive changes the
// draft never saw. A root draft has no base at all, so any active version counts.
// Returns that active version to name it, or null when there is nothing to warn
// about (no active version, or the draft's base is still the active one). Only a
// draft has a base to compare with: an archived version being re-activated has its
// own lineage and replaces whatever is live by design — the backend locks the live
// row for that flip, so there is nothing to warn about either.
export const getPublishConflict = (
  version: IRoutingConfigVersion,
  active: IRoutingConfigVersion | null,
): IRoutingConfigVersion | null => {
  if (version.status !== 'draft' || !active) return null;

  return active.id === version.parent_version_id ? null : active;
};

// "Same routing behaviour?" — compared in the PUT shape, where ids, uids and the
// array-or-string condition duality drop out while order and every field the
// engine reads stay in.
export const isSameConfig = (a: IRoutingConfigPolicy[], b: IRoutingConfigPolicy[]): boolean =>
  deepEqual(toPutPayload(toEditorPolicies(a)), toPutPayload(toEditorPolicies(b)));
